/**
 * Company › Owned arches › one arche's TEAM (ADR-026).
 *
 * Adding and role changes: a Company-owned arche has no owner collaborators,
 * and the CLI's `collaborators` commands authenticate with an API key, which
 * Company governance refuses — so this is where its owners and admins add a
 * publisher or a viewer (by email or username) and move people between the
 * two. Owner is never offered (the Company governs).
 *
 * Offboarding: a publisher row outlives every Company and workspace
 * membership, so anyone who has left the Company is flagged here — and can be
 * revoked. Nothing is revoked on its own (a contractor is a legitimate
 * publisher), and revoking asks first.
 */

import { useId, useState, type FormEvent } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import type {
  CompanyArcheCollaboratorRole,
  CompanyArcheTeamMemberDto,
  CompanyOwnedArcheDto,
} from '@/lib/api';
import { ARCHE_ROLE_LABEL } from '../../lib/arche-governance-labels';
import {
  useAddCompanyArcheCollaborator,
  useChangeCompanyArcheCollaboratorRole,
  useRevokeCompanyArcheCollaborator,
} from '../../hooks/useArcheGovernance';
import { describeError, personLabel } from './shared';

/** The collaborator route's bounds (`identifierSchema` in routes/arches/collaborators.ts). */
const MAX_EMAIL_LENGTH = 254;
const MAX_USERNAME_LENGTH = 64;
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * What the person typed, as the collaborator route's identifier: an email if
 * it contains an `@` past the first character, otherwise a username (a
 * leading `@` is dropped). Null when there is nothing the route would accept,
 * so Add stays disabled rather than sending a request bound to fail.
 */
export function parseCollaboratorIdentifier(
  raw: string,
): { kind: 'email' | 'username'; value: string } | null {
  const value = raw.trim();
  const username = value.startsWith('@') ? value.slice(1) : null;
  if (username === null && value.includes('@')) {
    return value.length <= MAX_EMAIL_LENGTH && EMAIL_SHAPE.test(value)
      ? { kind: 'email', value }
      : null;
  }
  const name = username ?? value;
  return name.length >= 1 && name.length <= MAX_USERNAME_LENGTH && !/[\s@]/.test(name)
    ? { kind: 'username', value: name }
    : null;
}

/**
 * One member's role, changeable between Publisher and Viewer. The value
 * shown is always the server's: a failed change leaves it where it was.
 */
function TeamMemberRole({
  companyId,
  arche,
  member,
}: {
  companyId: string;
  arche: CompanyOwnedArcheDto;
  member: CompanyArcheTeamMemberDto;
}) {
  const change = useChangeCompanyArcheCollaboratorRole(companyId);
  // An owner row cannot exist on a company-owned arche; if one ever shows up,
  // it is named, never offered as a choice.
  if (member.role === 'owner') {
    return <span className="text-muted-foreground">{ARCHE_ROLE_LABEL.owner}</span>;
  }

  async function onChange(role: CompanyArcheCollaboratorRole): Promise<void> {
    if (role === member.role) return;
    try {
      await change.mutateAsync({
        archeId: arche.archeId,
        userId: member.userId,
        role,
      });
      toast({
        title: `${personLabel(member)} is now a ${ARCHE_ROLE_LABEL[role].toLowerCase()} on ${arche.name}`,
      });
    } catch (err) {
      toast({
        title: 'Could not change role',
        description: describeError(err),
        variant: 'destructive',
      });
    }
  }

  return (
    <select
      className="h-7 rounded-md border border-input bg-background px-1.5 text-xs"
      aria-label={`${personLabel(member)}’s role on ${arche.name}`}
      value={member.role}
      disabled={change.isPending}
      onChange={(e) => void onChange(e.target.value as CompanyArcheCollaboratorRole)}
    >
      <option value="publisher">{ARCHE_ROLE_LABEL.publisher}</option>
      <option value="viewer">{ARCHE_ROLE_LABEL.viewer}</option>
    </select>
  );
}

function AddTeamMemberForm({
  companyId,
  arche,
}: {
  companyId: string;
  arche: CompanyOwnedArcheDto;
}) {
  const add = useAddCompanyArcheCollaborator(companyId);
  const [who, setWho] = useState('');
  const [role, setRole] = useState<CompanyArcheCollaboratorRole>('publisher');
  const inputId = useId();
  const roleId = useId();
  const identifier = parseCollaboratorIdentifier(who);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!identifier) return;
    try {
      const result = await add.mutateAsync({ archeId: arche.archeId, identifier, role });
      toast({
        title: `Added ${identifier.value} to ${arche.name} as ${ARCHE_ROLE_LABEL[role].toLowerCase()}`,
        description: result.reactivated ? 'Their earlier access was restored.' : undefined,
      });
      setWho('');
    } catch (err) {
      toast({
        title: 'Could not add to the team',
        description: describeError(err),
        variant: 'destructive',
      });
    }
  }

  return (
    <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => void onSubmit(e)}>
      <div className="min-w-[14rem] flex-1 space-y-1">
        <Label htmlFor={inputId} className="text-xs">
          Add to the team
        </Label>
        <Input
          id={inputId}
          className="h-8 text-xs"
          placeholder="Email or username"
          autoComplete="off"
          maxLength={MAX_EMAIL_LENGTH}
          value={who}
          onChange={(e) => setWho(e.target.value)}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor={roleId} className="text-xs">
          Role
        </Label>
        <select
          id={roleId}
          className="h-8 rounded-md border border-input bg-background px-2 text-xs"
          value={role}
          onChange={(e) => setRole(e.target.value as CompanyArcheCollaboratorRole)}
        >
          <option value="publisher">{ARCHE_ROLE_LABEL.publisher}</option>
          <option value="viewer">{ARCHE_ROLE_LABEL.viewer}</option>
        </select>
      </div>
      <Button
        type="submit"
        size="sm"
        className="h-8 text-xs"
        disabled={!identifier || add.isPending}
      >
        {add.isPending ? 'Adding…' : 'Add'}
      </Button>
    </form>
  );
}

export function OwnedArcheTeam({
  companyId,
  arche,
  id,
}: {
  companyId: string;
  arche: CompanyOwnedArcheDto;
  /** For the toggle's `aria-controls`. */
  id?: string;
}) {
  const revoke = useRevokeCompanyArcheCollaborator(companyId);
  const [revoking, setRevoking] = useState<CompanyArcheTeamMemberDto | null>(null);

  if (arche.team.length === 0) {
    return (
      <div id={id} className="space-y-2">
        <p className="text-xs text-muted-foreground">Nobody is on this arche&apos;s team yet.</p>
        <AddTeamMemberForm companyId={companyId} arche={arche} />
      </div>
    );
  }

  async function confirmRevoke(m: CompanyArcheTeamMemberDto): Promise<void> {
    try {
      await revoke.mutateAsync({ archeId: arche.archeId, userId: m.userId });
      toast({ title: `Revoked ${personLabel(m)}’s access to ${arche.name}` });
    } catch (err) {
      toast({ title: 'Could not revoke', description: describeError(err), variant: 'destructive' });
    }
  }

  return (
    <div id={id} className="space-y-2">
      <ul className="divide-y rounded-md border text-xs" aria-label={`${arche.name} team`}>
        {arche.team.map((m) => (
          <li
            key={m.userId}
            className="flex flex-wrap items-center justify-between gap-2 px-3 py-1.5"
          >
            <span className="flex items-center gap-2">
              <span>{personLabel(m)}</span>
              <TeamMemberRole companyId={companyId} arche={arche} member={m} />
              {!m.affiliated && (
                <Badge
                  variant="destructive"
                  title="Not on this Company's roster and not in any of its linked workspaces"
                >
                  Not in this Company
                </Badge>
              )}
            </span>
            <Button
              size="sm"
              variant={m.affiliated ? 'ghost' : 'outline'}
              className="h-7 px-2 text-xs"
              disabled={revoke.isPending}
              aria-label={`Revoke ${personLabel(m)}’s access to ${arche.name}`}
              onClick={() => setRevoking(m)}
            >
              Revoke access
            </Button>
          </li>
        ))}
      </ul>
      <AlertDialog open={revoking !== null} onOpenChange={(o) => !o && setRevoking(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {revoking
                ? `Revoke ${personLabel(revoking)}’s access to ${arche.name}?`
                : 'Revoke access?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              They leave its team: they can no longer publish it, and if only its team and chosen
              people may use it, they lose that too unless they were given access some other way.
              They can be added back here.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const m = revoking;
                setRevoking(null);
                if (m) void confirmRevoke(m);
              }}
            >
              Revoke access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AddTeamMemberForm companyId={companyId} arche={arche} />
    </div>
  );
}
